import React from 'react';
import { Link } from 'react-router-dom';
import { CONTACT_INFO, COMPANY_INFO, TRUCK_SERVICES } from '../../utils/constants';
import './ServicePage.css';

const OneTonTruck: React.FC = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "1 Ton Pickup Truck Rental Dubai",
    "description": "Professional 1 ton pickup truck rental service in Dubai with tail lift for small deliveries, light moving tasks, and local transportation needs.",
    "provider": {
      "@type": "LocalBusiness",
      "name": COMPANY_INFO.name,
      "telephone": CONTACT_INFO.phone,
      "email": CONTACT_INFO.email,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": CONTACT_INFO.address,
        "addressLocality": "Dubai",
        "addressCountry": "UAE"
      }
    },
    "areaServed": {
      "@type": "City",
      "name": "Dubai"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "1 Ton Pickup Truck Rental",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "1 Ton Pickup Truck with Tail Lift",
            "description": "Perfect for small deliveries and light moving tasks"
          }
        }
      ]
    }
  };

  return (
    <div className="service-page">
      {/* Hero Section */}
      <section className="service-hero">
        <div className="container">
          <div className="hero-content">
            <h1>1 Ton Pickup Truck Rental Dubai</h1>
            <p>Perfect for small deliveries, light moving tasks, and local transportation needs across Dubai</p>
            <div className="hero-buttons">
              <a href={`tel:${CONTACT_INFO.phone}`} className="btn-primary">Call Now: {CONTACT_INFO.phone}</a>
              <a href={`mailto:${CONTACT_INFO.email}`} className="btn-secondary">Get Quote</a>
            </div>
          </div>
          <div className="hero-image">
            <img src="/1-ton-pickup-rental-truck-dubai.jpg" alt="1 Ton Pickup Truck Dubai" />
          </div>
        </div>
      </section>

      {/* Service Details */}
      <section className="service-details">
        <div className="container">
          <div className="service-overview">
            <h2>1 Ton Pickup Truck Rental Service in Dubai</h2>
            <p>
              Our 1 ton pickup truck rental service in Dubai is perfect for small businesses, individuals, 
              and companies that need reliable transportation for light cargo, small deliveries, and local 
              moving tasks. With professional drivers and well-maintained vehicles, we ensure your goods 
              reach their destination safely and on time. For larger requirements, consider our <Link to="/3-ton-truck">3 ton pickup truck</Link> or <Link to="/5-ton-truck">5 ton pickup truck</Link> services.
            </p>
          </div>

          <div className="service-features">
            <h3>Key Features of Our 1 Ton Pickup Trucks</h3>
            <div className="features-grid">
              <div className="feature-item">
                <div className="feature-icon">🚛</div>
                <h4>Tail Lift Available</h4>
                <p>Easy loading and unloading with hydraulic tail lift system</p>
              </div>
              <div className="feature-item">
                <div className="feature-icon">📦</div>
                <h4>Open & Box Options</h4>
                <p>Choose between open bed or covered box based on your needs</p>
              </div>
              <div className="feature-item">
                <div className="feature-icon">⏰</div>
                <h4>24/7 Service</h4>
                <p>Round-the-clock availability for urgent delivery needs</p>
              </div>
              <div className="feature-item">
                <div className="feature-icon">👨‍💼</div>
                <h4>Professional Driver</h4>
                <p>Experienced and licensed drivers with local area knowledge</p>
              </div>
            </div>
          </div>

          <div className="service-applications">
            <h3>Perfect For</h3>
            <div className="applications-grid">
              <div className="application-item">
                <h4>🏪 Small Business Deliveries</h4>
                <p>Ideal for restaurants, retail stores, and small businesses delivering products to customers</p>
              </div>
              <div className="application-item">
                <h4>🏠 Light Moving Tasks</h4>
                    <p>Perfect for moving small furniture, appliances, and personal belongings. Learn more about <Link to="/best-movers-packers-dubai-2025">professional moving services in Dubai</Link>.</p>
              </div>
              <div className="application-item">
                <h4>📦 Package Delivery</h4>
                <p>Efficient for courier services and package delivery across Dubai. Read our <Link to="/pickup-truck-rental-guide-dubai">complete pickup truck rental guide</Link> for more information.</p>
              </div>
              <div className="application-item">
                <h4>🛠️ Equipment Transport</h4>
                <p>Great for transporting small tools, equipment, and supplies</p>
              </div>
            </div>
          </div>

          <div className="service-areas">
            <h3>Service Areas in Dubai</h3>
            <p>We provide 1 ton pickup truck rental services across all major areas of Dubai:</p>
            <div className="areas-list">
              <div className="area-group">
                <h4>Central Dubai</h4>
                <p>Downtown Dubai, DIFC, Business Bay, Jumeirah, Marina, DSO</p>
              </div>
              <div className="area-group">
                <h4>Northern Emirates</h4>
                <p>Deira, Bur Dubai, Karama, Satwa, Al Quoz, Mirdif</p>
              </div>
              <div className="area-group">
                <h4>New Dubai</h4>
                <p>Dubai Hills, Dubai Sports City, JVC, JLT, Motor City, Arabian Ranches</p>
              </div>
              <div className="area-group">
                <h4>Industrial Areas</h4>
                <p>Al Quoz Industrial, Jebel Ali, DIP, DIC, DWC, Silicon Oasis</p>
              </div>
            </div>
          </div>

          <div className="pricing-info">
            <h3>Competitive Pricing</h3>
            <p>
              Our 1 ton pickup truck rental rates are competitive and transparent. We offer flexible 
              pricing options including hourly, daily, and weekly rentals. Contact us for a detailed 
              quote based on your specific requirements.
            </p>
            <div className="pricing-features">
              <div className="pricing-item">
                <span className="icon">✅</span>
                <span>No hidden charges</span>
              </div>
              <div className="pricing-item">
                <span className="icon">✅</span>
                <span>Fuel included</span>
              </div>
              <div className="pricing-item">
                <span className="icon">✅</span>
                <span>Driver included</span>
              </div>
              <div className="pricing-item">
                <span className="icon">✅</span>
                <span>Insurance covered</span>
              </div>
            </div>
          </div>

          <div className="booking-process">
            <h3>How to Book</h3>
            <div className="process-steps">
              <div className="step">
                <div className="step-number">1</div>
                <h4>Contact Us</h4>
                <p>Call or email us with your pickup and delivery details</p>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <h4>Get Quote</h4>
                <p>Receive a detailed quote with pricing and timing</p>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <h4>Confirm Booking</h4>
                <p>Confirm your booking and receive confirmation details</p>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <h4>Service Delivery</h4>
                <p>Our driver arrives on time with the 1 ton pickup truck</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Book Your 1 Ton Pickup Truck?</h2>
            <p>Contact us now for reliable and professional 1 ton pickup truck rental services in Dubai</p>
            <div className="cta-buttons">
              <a href={`tel:${CONTACT_INFO.phone}`} className="btn-primary">Call: {CONTACT_INFO.phone}</a>
              <a href={`mailto:${CONTACT_INFO.email}`} className="btn-secondary">Email Us</a>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="related-services">
        <div className="container">
          <h3>Other Truck Rental Services</h3>
          <div className="services-grid">
            {TRUCK_SERVICES.filter(s => s.id !== '1-ton').map((service) => (
              <Link key={service.id} to={`/${service.id}-truck`} className="service-card">
                <img src={service.image} alt={service.title} />
                <h4>{service.title}</h4>
                <p>{service.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Related Blog Posts */}
      <section className="related-blog-posts">
        <div className="container">
          <h3>Related Articles</h3>
          <div className="blog-posts-grid">
                <Link to="/pickup-truck-rental-guide-dubai" className="blog-post-card">
                  <div className="blog-post-content">
                    <h4>The Ultimate Guide to Pickup Truck Rental in Dubai</h4>
                    <p>Complete guide to renting pickup trucks in Dubai. Learn about truck sizes, pricing, and requirements.</p>
                    <span className="read-more">Read More →</span>
                  </div>
                </Link>
                <Link to="/best-movers-packers-dubai-2025" className="blog-post-card">
                  <div className="blog-post-content">
                    <h4>Best Movers and Packers in Dubai 2025</h4>
                    <p>Discover the top-rated movers and packers in Dubai offering professional home shifting services.</p>
                    <span className="read-more">Read More →</span>
                  </div>
                </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OneTonTruck;
